/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Observable, Subscription, of } from 'rxjs';
import {
  TESTER_NAVIGATION_SIDEBAR_SERVICE,
  TesterNavigationSidebarService,
  TesterNavigationSidebarUrlKey,
} from '../tester-navigation-sidebar.service';

@Component({
  selector: 'ngf-tester-navigation-sidebar-menu-item-child',
  standalone: true,
  templateUrl: './tester-navigation-sidebar-menu-item-child.component.html',
  styleUrls: ['./tester-navigation-sidebar-menu-item-child.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { role: 'listitem' },
})
export class TesterNavigationSidebarMenuItemChildComponent implements OnInit, OnChanges, OnDestroy {
  @Input() href: string | null = null;
  @Input() activeKey: TesterNavigationSidebarUrlKey | null = null;

  @HostBinding('class.active') active = false;

  /**
   * FLO-54052/FLO-54098: absolute (cross-app) links must carry a target so the host app's AngularJS
   * `html5Mode` link rewriting leaves them alone — the uTest app now lives on the same origin under
   * `/utest/`, so without this the click is swallowed by the tester-app router and nothing happens.
   */
  get externalHrefTarget(): string | null {
    return this.href && /^https?:\/\//.test(this.href) ? '_self' : null;
  }

  private sub = Subscription.EMPTY;

  constructor(
    @Inject(TESTER_NAVIGATION_SIDEBAR_SERVICE) private sidebarService: TesterNavigationSidebarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscribeActive();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeKey'] && !changes['activeKey'].firstChange) {
      this.subscribeActive();
    }
  }

  /** Exposed so parent menu-item can combineLatest over its children's active streams. */
  isActive$(): Observable<boolean> {
    return this.activeKey ? this.sidebarService.isActive$(this.activeKey) : of(false);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private subscribeActive(): void {
    this.sub.unsubscribe();
    this.sub = this.isActive$().subscribe(active => {
      this.active = active;
      this.cdr.markForCheck();
    });
  }
}
